/* ==========================================================================
   Chrune personalisation tool

   Choose a piece, write the engraving, read it back, add to cart. The variant
   is resolved from the data the section printed, so the tool never guesses.

   The engraving rules are the site's own: allowed characters come from
   chrune-engrave-rules.js, the 14 character limit from the product metafield,
   every field the chosen piece offers must be filled, and the number of
   visible fields follows the chosen option the same way the product page
   does.

   The frame on the left opens on the piece's own presentation photograph, the
   studio shot the merchant put first. It only leaves it once the visitor has
   touched an option the photographs actually depend on, and from then on it
   follows what is picked, from the same map the product page reads. One
   photograph replaces another through a short blur.

   The symbol keyboard is the product page's, rendered once by the section. A
   tile types its own file code, M02 for instance, into the field it was
   opened from, and the manufacturer engraves that code as a single
   character. So a field is measured in units rather than in characters: a
   known code counts as one, anything else counts as itself.

   Everything is built with DOM nodes rather than markup strings: product
   titles and option values go in as text, never as HTML.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-ptool]');
  if (!root) return;

  var pieces = [];
  try {
    pieces = JSON.parse(root.querySelector('[data-ptool-data]').textContent);
  } catch (e) {
    return;
  }
  if (!pieces.length) return;

  var copy = root.querySelector('[data-ptool-copy]');
  var engraveHost = root.querySelector('[data-engrave-host]');
  var optionsHost = root.querySelector('[data-options-host]');
  var preview = root.querySelector('[data-ptool-preview]');
  var recapHost = root.querySelector('[data-recap]');
  var stepError = root.querySelector('[data-ptool-error]');
  var addStatus = root.querySelector('[data-ptool-status]');

  var visualImage = root.querySelector('[data-chosen-image]');
  var visualMedia = root.querySelector('.ptool__visual-media');
  var visualName = root.querySelector('[data-chosen-name]');
  var visualVibe = root.querySelector('[data-chosen-vibe]');
  var visualTimer = null;
  var visualHold = null;
  var visualEngaged = false;

  var symbolPanel = root.querySelector('[data-symbols]');
  var toggleTemplate = root.querySelector('[data-symbols-toggle-template]');
  var symbolCodes = [];
  var symbolGlyphs = {};
  var activeInput = null;

  var chosen = -1;

  function label(name) {
    return (copy && copy.getAttribute('data-' + name)) || '';
  }

  function rules() {
    return window.CHRUNE_ENGRAVE || null;
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function show(node, message, ok) {
    if (!node) return;
    node.textContent = message || '';
    node.classList.toggle('is-ok', !!ok);
    node.hidden = !message;
  }

  function panel(step) {
    return root.querySelector('[data-panel="' + step + '"]');
  }

  function goTo(step) {
    [1, 2, 3].forEach(function (n) {
      var p = panel(n);
      if (p) p.hidden = n !== step;
      var dot = root.querySelector('[data-step-dot="' + n + '"]');
      if (dot) {
        dot.classList.toggle('is-current', n === step);
        dot.classList.toggle('is-done', n < step);
      }
    });

    if (step !== 2) closeSymbols();

    root.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  function piece() {
    return pieces[chosen];
  }

  function maxChars(p) {
    var n = parseInt(p && p.maxChars, 10);
    return isNaN(n) || n <= 0 ? null : n;
  }

  /* ------------------------------------------------------------------ *
   * Symbols
   * ------------------------------------------------------------------ */

  /* The codes and their images are read from the keyboard itself, so the
     list can only ever be the one the section rendered. */
  if (symbolPanel) {
    Array.prototype.forEach.call(symbolPanel.querySelectorAll('[data-symbol]'), function (key) {
      var code = key.getAttribute('data-symbol');
      var image = key.querySelector('img');
      if (!code || symbolCodes.indexOf(code) !== -1) return;
      symbolCodes.push(code);
      if (image) symbolGlyphs[code] = image.getAttribute('src');
    });
    /* Longest first: a code that starts with another must still match whole. */
    symbolCodes.sort(function (a, b) { return b.length - a.length; });
    symbolPanel.hidden = false;
  }

  function symbolsFor(p) {
    return !!(symbolPanel && symbolCodes.length && p && p.symbols);
  }

  function symbolsOn() {
    return symbolsFor(piece());
  }

  /* One unit is what the manufacturer engraves as one character: a symbol
     code, or a single character. */
  function unitsOf(value) {
    if (!symbolsOn()) return String(value).split('');

    var units = [];
    var i = 0;
    while (i < value.length) {
      var matched = '';
      for (var c = 0; c < symbolCodes.length; c++) {
        if (value.substr(i, symbolCodes[c].length) === symbolCodes[c]) {
          matched = symbolCodes[c];
          break;
        }
      }
      if (matched) {
        units.push(matched);
        i += matched.length;
      } else {
        units.push(value.charAt(i));
        i += 1;
      }
    }
    return units;
  }

  function lengthOf(value) {
    return unitsOf(value).length;
  }

  /* The native maxlength counts characters, so on a piece with symbols it is
     only a last resort ceiling. The limit itself is applied here, on units,
     with the caret left where the visitor put it. */
  function clampField(input, limit) {
    if (!limit || !symbolsOn()) return;
    var units = unitsOf(input.value);
    if (units.length <= limit) return;

    var caret = input.selectionStart;
    input.value = units.slice(0, limit).join('');
    if (typeof caret !== 'number' || caret > input.value.length) caret = input.value.length;
    try { input.setSelectionRange(caret, caret); } catch (e) {}
  }

  function makeToggle() {
    if (!toggleTemplate || !toggleTemplate.content) return null;
    var node = toggleTemplate.content.firstElementChild;
    return node ? node.cloneNode(true) : null;
  }

  function syncSymbolKeys() {
    if (!symbolPanel) return;
    var limit = maxChars(piece());
    var full = !!(activeInput && limit && lengthOf(activeInput.value) >= limit);
    Array.prototype.forEach.call(symbolPanel.querySelectorAll('[data-symbol]'), function (key) {
      key.disabled = full;
    });
  }

  function closeSymbols() {
    activeInput = null;
    if (!symbolPanel) return;
    symbolPanel.classList.remove('is-open');
    Array.prototype.forEach.call(root.querySelectorAll('[data-symbols-toggle]'), function (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  function toggleSymbols(toggle) {
    if (!symbolPanel) return;
    var field = toggle.closest('.field');
    var input = field && field.querySelector('[data-engrave-field-input]');
    var wasOpen = toggle.getAttribute('aria-expanded') === 'true';

    closeSymbols();
    if (wasOpen || !input || input.disabled) return;

    activeInput = input;
    toggle.setAttribute('aria-expanded', 'true');
    symbolPanel.classList.add('is-open');
    syncSymbolKeys();
    input.focus();
  }

  /* Inserted at the caret, and refused rather than trimmed when it would not
     fit: a half inserted code would be engraved as letters. */
  function insertSymbol(code) {
    if (!activeInput || activeInput.disabled) return;

    var input = activeInput;
    var limit = maxChars(piece());
    var value = input.value;
    var start = typeof input.selectionStart === 'number' ? input.selectionStart : value.length;
    var end = typeof input.selectionEnd === 'number' ? input.selectionEnd : value.length;
    var next = value.slice(0, start) + code + value.slice(end);

    if (limit && lengthOf(next) > limit) return;

    input.value = next;
    var caret = start + code.length;
    input.focus();
    try { input.setSelectionRange(caret, caret); } catch (e) {}
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /* A code is drawn in the preview as its symbol, never as the letters the
     field carries. */
  function paintChip(chip, value) {
    chip.textContent = '';

    var run = '';
    var hasGlyph = false;

    function flushRun() {
      if (!run) return;
      chip.appendChild(document.createTextNode(run));
      run = '';
    }

    unitsOf(value).forEach(function (unit) {
      if (!symbolGlyphs[unit]) {
        run += unit;
        return;
      }
      flushRun();
      hasGlyph = true;
      var glyph = document.createElement('img');
      glyph.className = 'engrave__glyph';
      glyph.src = symbolGlyphs[unit];
      glyph.alt = '';
      chip.appendChild(glyph);
    });

    flushRun();
    chip.classList.toggle('has-glyph', hasGlyph);
  }

  /* ------------------------------------------------------------------ *
   * Fields
   * ------------------------------------------------------------------ */

  function currentValues(p) {
    return (p.options || []).map(function (opt, i) {
      var select = optionsHost.querySelector('[data-opt="' + i + '"]');
      if (select) return select.value;
      return opt.values && opt.values.length ? opt.values[0] : '';
    });
  }

  function matchVariant(p) {
    var wanted = currentValues(p);
    var found = null;
    (p.variants || []).forEach(function (v) {
      if (found || !v.available) return;
      var vals = v.o || [];
      if (vals.length !== wanted.length) return;
      for (var i = 0; i < wanted.length; i++) {
        if (vals[i] !== wanted[i]) return;
      }
      found = v;
    });
    return found;
  }

  function allInputs() {
    return Array.prototype.slice.call(engraveHost.querySelectorAll('[data-engrave-field-input]'));
  }

  /* Only the fields actually offered count for the preview, the checks and
     the properties sent to the cart. */
  function engraveInputs() {
    return allInputs().filter(function (input) {
      var field = input.closest('.field');
      return !input.disabled && !(field && field.hidden);
    });
  }

  /* Drives the engraving grid: four fields must read as a 2 by 2 block. */
  function markFieldCount() {
    engraveHost.setAttribute('data-fields', String(engraveInputs().length));
  }

  /* An option such as "3 hearts" or "2 engraved sides" says how many fields
     belong to the chosen variant. Same reading as the product page. */
  function fieldCount(p) {
    var count = null;
    currentValues(p).forEach(function (value) {
      var lower = String(value).toLowerCase();
      if (lower.indexOf('no engraving') !== -1 || lower.indexOf('sans gravure') !== -1) {
        count = 0;
        return;
      }
      var match = lower.match(/^(\d+)\s/);
      if (match) {
        var n = parseInt(match[1], 10);
        if (!isNaN(n) && (count === null || n < count)) count = n;
      }
    });
    return count;
  }

  function syncFields(p) {
    var count = fieldCount(p);
    if (count !== null) {
      allInputs().forEach(function (input, i) {
        var field = input.closest('.field');
        var visible = i < count;
        if (field) field.hidden = !visible;
        if (visible) {
          input.disabled = false;
        } else {
          input.value = '';
          input.disabled = true;
        }
      });
    }
    markFieldCount();
  }

  /* The preview only exists once there is something to show. */
  function refreshPreview() {
    if (!preview) return;
    var values = engraveInputs()
      .map(function (input) { return input.value.trim(); })
      .filter(Boolean);

    Array.prototype.forEach.call(preview.querySelectorAll('[data-chip]'), function (chip) {
      chip.remove();
    });

    values.forEach(function (value) {
      var chip = el('span', 'engrave__chip');
      chip.setAttribute('data-chip', '');
      paintChip(chip, value);
      preview.appendChild(chip);
    });

    preview.classList.toggle('is-visible', values.length > 0);
  }

  /* ------------------------------------------------------------------ *
   * The frame
   * ------------------------------------------------------------------ */

  /* A photograph carries the option values it shows as index=value pairs,
     the index being its option's place in the list of selects. */
  function imageRules(spec) {
    if (!spec) return [];
    return String(spec).split('|').map(function (part) {
      var at = part.indexOf('=');
      return { index: parseInt(part.slice(0, at), 10), value: part.slice(at + 1) };
    });
  }

  /* Whether one option is an option the photographs depend on. Picking a
     metal or a cord colour changes what there is to look at; picking a ring
     size does not, and must not pull the frame off the presentation shot. */
  function optionShowsInImages(p, index) {
    return (p.images || []).some(function (item) {
      return imageRules(item.m).some(function (rule) { return rule.index === index; });
    });
  }

  /* The photograph that answers the most of the combination wins: one shot
     per colour and per cord comes before one shot per colour. Between two
     equally precise ones the latest added wins, which is the plain shot of
     the piece on white rather than the older studio or worn photograph, and
     that is what a visitor comparing finishes wants to look at. A photograph
     naming no option is a last resort, and the piece's own lead image closes
     the list. */
  function imageFor(p) {
    var list = p.images || [];
    if (!list.length) return p.image || '';

    var picked = currentValues(p).map(function (value) {
      return String(value).trim().toLowerCase();
    });

    var best = null;
    var bestScore = -1;
    var plain = null;

    list.forEach(function (item) {
      var rule = imageRules(item.m);

      for (var i = 0; i < rule.length; i++) {
        if (picked[rule[i].index] !== rule[i].value) return;
      }

      if (!rule.length) {
        if (!plain) plain = item;
        return;
      }
      if (rule.length >= bestScore) {
        bestScore = rule.length;
        best = item;
      }
    });

    var found = best || plain;
    return found ? found.src : (p.image || '');
  }

  function paintVisual(src) {
    if (!visualImage) return;
    if (src) {
      visualImage.src = src;
      visualImage.hidden = false;
    } else {
      visualImage.removeAttribute('src');
      visualImage.hidden = true;
    }
  }

  /* Out of focus, swapped behind the blur, back into focus. The blur is held
     until the new photograph has arrived, so one still loading is never seen
     as an empty frame, with a ceiling on that wait. Timers rather than
     animation frames: a frame callback does not run while the tab is in the
     background, and the frame must come back sharp either way. */
  function swapVisual(src) {
    if (!visualImage || !src) return;
    if (visualImage.getAttribute('src') === src) return;

    if (!visualMedia || reducedMotion()) {
      paintVisual(src);
      return;
    }

    visualMedia.classList.add('is-swapping');
    window.clearTimeout(visualTimer);
    window.clearTimeout(visualHold);

    visualTimer = window.setTimeout(function () {
      paintVisual(src);

      function sharpen() {
        window.clearTimeout(visualHold);
        visualMedia.classList.remove('is-swapping');
      }

      if (visualImage.complete) {
        window.setTimeout(sharpen, 20);
      } else {
        visualImage.addEventListener('load', sharpen, { once: true });
        visualImage.addEventListener('error', sharpen, { once: true });
        visualHold = window.setTimeout(sharpen, 1600);
      }
    }, 140);
  }

  /* ------------------------------------------------------------------ *
   * Step 2
   * ------------------------------------------------------------------ */

  /* A piece is presented by the photograph the merchant put first, the studio
     shot. Nothing has been chosen yet, so nothing else has been earned. */
  function buildVisual(p) {
    window.clearTimeout(visualTimer);
    window.clearTimeout(visualHold);
    if (visualMedia) visualMedia.classList.remove('is-swapping');
    visualEngaged = false;
    paintVisual(p.image || imageFor(p));
    if (visualName) visualName.textContent = p.name || p.title;
    if (visualVibe) visualVibe.textContent = p.vibe || '';
  }

  function buildForm(p) {
    closeSymbols();
    engraveHost.innerHTML = '';
    optionsHost.innerHTML = '';
    var limit = maxChars(p);
    var withSymbols = symbolsFor(p);

    (p.labels || []).forEach(function (name, index) {
      var field = el('div', 'field');
      var row = el('div', 'ptool__label-row');
      var id = 'PtoolEngrave-' + index;

      var lab = el('label', 'field__label', name);
      lab.setAttribute('for', id);
      row.appendChild(lab);

      var counter = el('span', 'ptool__count', limit ? '0/' + limit : '0');
      counter.setAttribute('aria-hidden', 'true');
      counter.setAttribute('data-count', '');
      row.appendChild(counter);

      field.appendChild(row);

      var input = el('input', 'input');
      input.type = 'text';
      input.id = id;
      input.autocomplete = 'off';
      input.setAttribute('data-engrave-field-input', '');
      input.setAttribute('data-label', name);
      if (limit) input.maxLength = withSymbols ? limit * 3 : limit;
      field.appendChild(input);

      if (withSymbols) {
        var toggle = makeToggle();
        if (toggle) field.appendChild(toggle);
      }

      engraveHost.appendChild(field);
    });

    (p.options || []).forEach(function (opt, index) {
      if (!opt.values || opt.values.length < 2) return;

      var field = el('div', 'field');
      var id = 'PtoolOpt-' + index;

      var lab = el('label', 'field__label', opt.name);
      lab.setAttribute('for', id);
      field.appendChild(lab);

      var select = el('select', 'input');
      select.id = id;
      select.setAttribute('data-opt', String(index));
      opt.values.forEach(function (value) {
        var option = el('option', null, value);
        option.value = value;
        select.appendChild(option);
      });
      field.appendChild(select);

      optionsHost.appendChild(field);
    });

    syncFields(p);
    refreshPreview();
  }

  function checkCharacters() {
    var api = rules();
    if (!api) return null;

    var bad = null;
    engraveInputs().forEach(function (input) {
      var found = api.forbidden(input.value);
      input.setAttribute('aria-invalid', found ? 'true' : 'false');
      if (found && !bad) bad = { char: found, input: input };
    });
    return bad;
  }

  /* Every field the piece offers is engraved, so every one must be filled:
     an empty one would reach the manufacturer as an engraving with nothing
     on it. */
  function firstEmpty() {
    var empty = null;
    engraveInputs().forEach(function (input) {
      if (input.value.trim()) return;
      input.setAttribute('aria-invalid', 'true');
      if (!empty) empty = input;
    });
    return empty;
  }

  /* ------------------------------------------------------------------ *
   * Step 3
   * ------------------------------------------------------------------ */

  function buildRecap(p, variant) {
    recapHost.innerHTML = '';

    /* The recap shows the photograph the frame is on, so it reads back what
       the visitor has been looking at. */
    var src = (visualImage && visualImage.getAttribute('src')) || p.image;

    if (src) {
      var img = el('img');
      img.src = src;
      img.alt = '';
      recapHost.appendChild(img);
    }

    var body = el('div', 'ptool__recap-body');

    var head = el('div', 'ptool__recap-head');
    head.appendChild(el('p', 'ptool__recap-title', p.title));
    if (variant && variant.price) {
      head.appendChild(el('span', 'ptool__recap-price', variant.price));
    }
    body.appendChild(head);

    (p.options || []).forEach(function (opt, i) {
      if (!opt.values || opt.values.length < 2) return;
      var select = optionsHost.querySelector('[data-opt="' + i + '"]');
      var row = el('div', 'ptool__recap-row');
      row.appendChild(el('span', 'ptool__recap-key', opt.name));
      row.appendChild(el('span', 'ptool__recap-value', select ? select.value : ''));
      body.appendChild(row);
    });

    var written = engraveInputs().filter(function (input) { return input.value.trim(); });

    if (written.length) {
      written.forEach(function (input) {
        var row = el('div', 'ptool__recap-row');
        row.appendChild(el('span', 'ptool__recap-key', input.getAttribute('data-label')));
        row.appendChild(el('span', 'ptool__recap-value', input.value.trim()));
        body.appendChild(row);
      });
    } else {
      var row = el('div', 'ptool__recap-row');
      row.appendChild(el('span', 'ptool__recap-key', label('label-engraving')));
      row.appendChild(el('span', 'ptool__recap-value is-plain', label('label-none')));
      body.appendChild(row);
    }

    recapHost.appendChild(body);
  }

  function addToCart() {
    var p = piece();
    var variant = matchVariant(p);
    if (!variant) {
      show(addStatus, label('msg-error'), false);
      return;
    }

    if (checkCharacters() || firstEmpty()) {
      show(addStatus, label('msg-required'), false);
      return;
    }

    var properties = {};
    engraveInputs().forEach(function (input) {
      var value = input.value.trim();
      if (value) properties[input.getAttribute('data-label')] = value;
    });

    var button = root.querySelector('[data-ptool-add]');
    if (button) button.disabled = true;
    show(addStatus, '', false);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variant.id, quantity: 1, properties: properties })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('add');
        return response.json();
      })
      .then(function () {
        show(addStatus, label('msg-added'), true);
        document.dispatchEvent(new CustomEvent('chrune:cart-added'));
      })
      .catch(function () {
        show(addStatus, label('msg-error'), false);
      })
      .finally(function () {
        if (button) button.disabled = false;
      });
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  function goToRecap(p) {
    var variant = matchVariant(p);
    if (!variant) {
      show(stepError, label('msg-error'), false);
      return;
    }
    show(stepError, '', false);
    buildRecap(p, variant);
    show(addStatus, '', false);
    goTo(3);
  }

  root.addEventListener('click', function (event) {
    var symbolKey = event.target.closest('[data-symbol]');
    if (symbolKey) {
      insertSymbol(symbolKey.getAttribute('data-symbol'));
      return;
    }

    var symbolsToggle = event.target.closest('[data-symbols-toggle]');
    if (symbolsToggle) {
      toggleSymbols(symbolsToggle);
      return;
    }

    var pick = event.target.closest('[data-pick]');
    if (pick) {
      chosen = parseInt(pick.getAttribute('data-pick'), 10);
      Array.prototype.forEach.call(root.querySelectorAll('[data-pick]'), function (card) {
        card.classList.toggle('is-selected', card === pick);
      });
      var p = piece();
      buildForm(p);
      buildVisual(p);
      show(stepError, '', false);
      goTo(2);
      return;
    }

    var back = event.target.closest('[data-back]');
    if (back) {
      goTo(parseInt(back.getAttribute('data-back'), 10));
      return;
    }

    var next = event.target.closest('[data-next]');
    if (next) {
      var current = piece();
      var bad = checkCharacters();
      if (bad) {
        var api = rules();
        show(stepError, api ? api.fill(label('msg-charset'), bad.char) : label('msg-charset'), false);
        bad.input.focus();
        return;
      }

      var empty = firstEmpty();
      if (empty) {
        show(stepError, label('msg-required'), false);
        empty.focus();
        return;
      }

      goToRecap(current);
      return;
    }

    if (event.target.closest('[data-ptool-add]')) addToCart();
  });

  root.addEventListener('change', function (event) {
    var opt = event.target.closest('[data-opt]');
    if (!opt) return;

    closeSymbols();
    var p = piece();
    syncFields(p);

    /* The presentation shot holds the frame until an option that the
       photographs actually depend on has been touched. From then on the
       frame follows every change, including the ones that came before. */
    if (!visualEngaged && optionShowsInImages(p, parseInt(opt.getAttribute('data-opt'), 10))) {
      visualEngaged = true;
    }
    if (visualEngaged) swapVisual(imageFor(p));

    refreshPreview();
  });

  root.addEventListener('input', function (event) {
    var input = event.target.closest('[data-engrave-field-input]');
    if (!input) return;

    var limit = maxChars(piece());
    clampField(input, limit);

    var field = input.closest('.field');
    var counter = field && field.querySelector('[data-count]');
    var written = lengthOf(input.value);
    if (counter) {
      counter.textContent = limit ? written + '/' + limit : String(written);
      counter.classList.toggle('is-over', !!limit && written > limit);
    }

    if (input === activeInput) syncSymbolKeys();

    refreshPreview();

    var api = rules();
    if (!api) return;
    var found = api.forbidden(input.value);
    input.setAttribute('aria-invalid', found ? 'true' : 'false');
    if (found) {
      show(stepError, api.fill(label('msg-charset'), found), false);
    } else if (!checkCharacters()) {
      show(stepError, '', false);
    }
  });

  /* Escape closes the keyboard, as it does on the product page. */
  root.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (!symbolPanel || !symbolPanel.classList.contains('is-open')) return;
    closeSymbols();
  });
})();